/* eslint-disable react/jsx-key */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import tw from 'twin.macro';

import { useGetPaymentUrlQuery, usePrepaidGetProductsQuery, usePrepaidOrderCardMutation } from '@/app/api/prepaid';
import { getStep } from '@/app/store/slices/app';
import { setCustomError } from '@/app/store/slices/error';
import { wsStatuses } from '@/app/utils/constants';
import WarningIcon1 from '@/assets/images/icons/WarningIcon1';
import BlueCard from '@/assets/images/prepaid_blue.webp';
import GrayCard from '@/assets/images/prepaid_gray.webp';
import GreenCard from '@/assets/images/prepaid_green.webp';
import MintCard from '@/assets/images/prepaid_mint.webp';
import PinkCard from '@/assets/images/prepaid_pink.webp';
import { BodyText, Button, Modal, StepContentWrapper, SubTitle, Title } from '@/components';
import useCheckStatusPrepaid from '@/helper/hooks/useCheckStatusPrepaid';

function PrepaidCardTypePage() {
  const [cards, setCards] = useState([]);
  const [warningOpen, setWarningOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [choosenCard, setChoosenCard] = useState(null);
  const [fetchOrderCard, { isLoading: createCardLoading, isSuccess: createCardSuccess, error: orderCardError }] =
    usePrepaidOrderCardMutation();
  const [checkStatusPrepaid] = useCheckStatusPrepaid();
  const navigate = useNavigate();
  const step = useSelector(getStep);
  const { data, isFetching, isLoading, error } = usePrepaidGetProductsQuery();
  const { data: paymentUrl, error: getPaymentUrlError } = useGetPaymentUrlQuery(null, {
    skip: step !== wsStatuses.payment
  });
  const dispatch = useDispatch();

  const handleConvertData = cards => {
    const newArray = [];
    cards?.map(item => {
      const data = handleReturnTitleCommisionImg(item.product_code);
      const { img, title, commission } = data;
      newArray.push({
        img: img,
        title: `${title} долларов`,
        caption: (
          <span>
            Комиссия за выпуск — <br />
            {commission} тенге
          </span>
        ),
        buttonText: `Купить за ${item.amount}$`,
        card_type: item.product_code
      });
    });
    return newArray;
  };

  const handleReturnTitleCommisionImg = card_type => {
    switch (card_type) {
      case 'PREPAID_50':
        return {
          img: PinkCard,
          title: 50,
          commission: '3 000'
        };
      case 'PREPAID_100':
        return {
          img: GreenCard,
          title: 100,
          commission: '5 000'
        };
      case 'PREPAID_200':
        return {
          img: BlueCard,
          title: 200,
          commission: '7 000'
        };
      case 'PREPAID_300':
        return {
          img: MintCard,
          title: 300,
          commission: '9 000'
        };
      case 'PREPAID_400':
        return {
          img: GrayCard,
          title: 400,
          commission: '11 000'
        };
      default:
        break;
    }
  };

  const handleCreateCard = () => {
    const product_code = choosenCard;
    fetchOrderCard({ product_code });
  };

  useEffect(() => {
    paymentUrl && window.open(paymentUrl?.data?.payment_url, '_blank');
  }, [paymentUrl]);

  useEffect(() => {
    if (error) {
      dispatch(setCustomError(error?.data?.error));
    }
  }, [error]);

  useEffect(() => {
    orderCardError?.data?.error?.code === 'NO_ACTIVE_APPLICATION' && setWarningOpen(true);
  }, [orderCardError]);

  useEffect(() => {
    setCards(handleConvertData(data?.data?.products));
  }, [data]);

  return (
    <>
      <StepContentWrapper twStyles={tw`text-center px-7 pb-[48px]`}>
        <Title text='Выберите номинал карты' variant='bold' twStyle={tw`mt-[30px] sm:mt-[48px] `} />
        <div tw='grid sm:grid-cols-3 grid-cols-1 w-full justify-around gap-5 my-5 mt-[20px]'>
          {isFetching || isLoading
            ? cards?.map(card => (
                <div
                  className='skeleton'
                  tw='animate-pulse sm:w-[250px] w-full h-[300px] bg-cardcontainer rounded-2xl'
                ></div>
              ))
            : cards?.map(card => (
                <div tw='bg-cardcontainer p-5 flex flex-col gap-5 rounded-2xl sm:max-w-[250px]' key={card.card_type}>
                  <div tw=''>
                    <img src={card?.img} alt='' />
                  </div>
                  <BodyText text={card?.title} variant={'bold'} />
                  <BodyText text={card?.caption} twStyle={tw`text-secondary`} />
                  <Button
                    variant={'secondary'}
                    twStyle={tw`min-w-[150px]`}
                    onClick={() => {
                      setChoosenCard(card?.card_type);
                      setOpen(true);
                    }}
                  >
                    {card?.buttonText}
                  </Button>
                </div>
              ))}
        </div>
        {data?.data?.currency_rate && (
          <div tw='bg-cardcontainer flex p-5 rounded-2xl items-center gap-3'>
            <WarningIcon1 color={'#70B6F6'} />
            <BodyText
              text={`Обменный курс — ${data?.data?.currency_rate} тенге за доллар`}
              twStyle={tw`text-secondary`}
            />
          </div>
        )}
        <Modal
          open={open}
          setOpen={setOpen}
          twStyle={tw`sm:max-w-[400px] w-[95vw] rounded-2xl`}
          withBlur={true}
          headerStyle={tw`mb-5`}
        >
          <div tw='flex flex-col gap-5'>
            <SubTitle text={'Подтвердите свой выбор'} variant={'bold'} />
            <SubTitle
              text={
                <span>
                  Вы выбрали карту номиналом{' '}
                  <span tw='font-bold text-green whitespace-nowrap'>{choosenCard?.split('_')[1]} $</span>
                  <br />
                  Cтоимость с учётом коммиссии &nbsp;
                  <span tw='font-bold text-green whitespace-nowrap'>
                    {data?.data?.products?.find(item => item?.product_code === choosenCard)?.amount} $
                  </span>
                </span>
              }
            />
            <Button
              variant={'secondary'}
              onClick={handleCreateCard}
              disabled={createCardLoading || createCardSuccess}
              loading={createCardLoading || createCardSuccess}
            >
              Подтверждаю
            </Button>
            {getPaymentUrlError && <p>{getPaymentUrlError?.data?.error?.description}</p>}
          </div>
        </Modal>
        <Modal
          setOpen={setWarningOpen}
          open={warningOpen}
          twStyle={tw`max-w-[400px] rounded-2xl`}
          withCloseIcon={false}
          outsideClose={false}
          withBlur={true}
        >
          <div tw='w-full flex flex-col gap-5 p-2 items-center'>
            <WarningIcon1 />
            <SubTitle
              variant={'bold'}
              text={
                <span>
                  Время активной сессии истекло.
                  <br /> Вам нужно пройти процесс заново для оформления Prepaid карты
                </span>
              }
            />
            <Button variant={'secondary'} onClick={() => navigate('/prepaid-card/apply')}>
              Пройти заново
            </Button>
          </div>
        </Modal>
      </StepContentWrapper>
    </>
  );
}

export default PrepaidCardTypePage;
